import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';

interface FeatureFlags {
  nftEnabled: boolean;
}

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  loading: boolean;
  setFlag: (key: keyof FeatureFlags, value: boolean) => Promise<void>;
}

const DEFAULT_FLAGS: FeatureFlags = {
  nftEnabled: false,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined);

export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }

    const docRef = doc(db, 'config', 'features');
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setFlags({
            nftEnabled: data.nftEnabled ?? DEFAULT_FLAGS.nftEnabled,
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to feature flags:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const setFlag = async (key: keyof FeatureFlags, value: boolean) => {
    if (!db) return;
    const docRef = doc(db, 'config', 'features');
    await setDoc(docRef, { [key]: value }, { merge: true });
  };

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
};
